import React, { useState } from 'react';
import ConfirmDestructiveDialog from '@/components/ConfirmDestructiveDialog';
import type { OrgMember } from '@/api/services/organizations';

type Props = {
  member: OrgMember;
  organizationName?: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
};

export default function RemoveOrganizationMemberModal({
  member,
  organizationName,
  onClose,
  onConfirm,
}: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const label = member.name?.trim() || member.email || 'this member';
  const orgLabel = organizationName || 'this organization';

  async function handleConfirm() {
    setSubmitting(true);
    setError('');
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove failed');
      setSubmitting(false);
    }
  }

  return (
    <ConfirmDestructiveDialog
      title="Remove member from organization"
      onClose={onClose}
      closeOnBackdrop={!submitting}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => void handleConfirm()}
            disabled={submitting}
          >
            {submitting ? 'Removing…' : 'Remove from organization'}
          </button>
        </>
      }
    >
      <p className="muted modal-hint">
        Remove <strong>{label}</strong> from <strong>{orgLabel}</strong>?
      </p>
      {error ? <div className="alert alert-error">{error}</div> : null}
      <ul className="removal-summary-list">
        <li>{label} will lose access to this organization.</li>
        <li>The user account remains active.</li>
        <li>Searches and recipients they own stay in the organization unless reassigned.</li>
        <li>Audit history for this organization is preserved.</li>
      </ul>
    </ConfirmDestructiveDialog>
  );
}
